import { useEffect, useRef } from "react";
import type { RoundSnapshot } from "../../domain/round/round-reducer.js";
import { liveRoundVersion } from "./live-round-event.js";

/** Live events that change what a follower sees of a round. */
const followedEventTypes = [
  "round.started",
  "score.set",
  "hole.entry.set",
  "hole.entry.revised",
  "metric.set",
  "result.declared",
  "scorekeeper.transferred",
  "contest.hole.conceded",
  "contest.match.conceded",
  "contest.remainder.agreed",
  "result.correction.proposed",
  "result.correction.responded",
  "result.correction.rejected",
  "result.correction.applied",
  "round.finished",
  "round.abandoned",
];

/** A burst of events (or the replay on connect) costs one reload. */
const RELOAD_DELAY_MS = 500;

/**
 * Follows a rival's round in play without leaving the page: an event newer
 * than the shown snapshot reloads the page data. No round, no stream.
 */
export function useRoundFollow(
  round: Pick<RoundSnapshot, "roundId" | "version"> | null,
  reload: () => void,
): void {
  const shown = useRef({ version: round?.version ?? 0, reload });
  useEffect(() => {
    shown.current = { version: round?.version ?? 0, reload };
  });
  const roundId = round?.roundId ?? null;
  useEffect(() => {
    if (!roundId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const source = new EventSource(`/api/v1/rounds/${roundId}/events`, {
      withCredentials: true,
    });
    const receive = (event: Event) => {
      if (!("data" in event) || typeof event.data !== "string") return;
      const version = liveRoundVersion(event.data);
      if (version === null || version <= shown.current.version) return;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        shown.current.reload();
      }, RELOAD_DELAY_MS);
    };
    followedEventTypes.forEach((type) =>
      source.addEventListener(type, receive),
    );
    return () => {
      if (timer !== null) clearTimeout(timer);
      source.close();
    };
  }, [roundId]);
}
